<template>
  <div class="session-results-page">
    <div class="page-header">
      <div class="header-content">
        <div class="back-button">
          <Button 
            icon="pi pi-arrow-left" 
            class="p-button-text" 
            @click="$router.go(-1)"
            label="Назад"
          />
        </div>
        <div class="header-info">
          <h1>{{ session?.name || 'Результати сесії' }}</h1>
          <p v-if="session?.test_type?.category === 'gallup_q12'">
            Gallup Q12 - Результати залуженості співробітників
          </p>
          <p v-else-if="session?.test_type?.category === 'enps'">
            eNPS - Результати Net Promoter Score
          </p>
          <p v-else>
            Результати тестування
          </p>
        </div>
      </div>
    </div>

    <div class="content-section">
      <!-- Статистика сесії -->
      <div class="session-stats">
        <div class="stats-grid">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="pi pi-users"></i>
            </div>
            <div class="stat-content">
              <h3>{{ session?.participants_count || 0 }}</h3>
              <p>Учасників</p>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-icon">
              <i class="pi pi-check-circle"></i>
            </div>
            <div class="stat-content">
              <h3>{{ session?.completed_count || 0 }}</h3>
              <p>Завершено</p>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-icon">
              <i class="pi pi-percentage"></i>
            </div>
            <div class="stat-content">
              <h3>{{ completionRate }}%</h3>
              <p>Відсоток завершення</p>
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-icon">
              <i class="pi pi-calendar"></i>
            </div>
            <div class="stat-content">
              <h3>{{ sessionPeriod }}</h3>
              <p>Період проведення</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Результати Gallup Q12 -->
      <div v-if="session?.test_type?.category === 'gallup_q12'" class="gallup-results">
        <h2>Результати Gallup Q12</h2>
        
        <!-- Загальний бал -->
        <div class="overall-score-section">
                     <div class="score-circle">
             <div class="circle-progress" :style="overallScoreStyle">
               <div class="circle-fill"></div>
             </div>
                         <div class="score-value">
               <span class="score-number">{{ averageOverallScore }}</span>
               <span class="score-label">Загальний бал</span>
             </div>
          </div>
          <div class="score-info">
            <h3>{{ getEngagementLevelLabel(averageEngagementLevel) }}</h3>
            <p>{{ getEngagementDescription(averageEngagementLevel) }}</p>
          </div>
        </div>

        <!-- Бали за категоріями -->
        <div class="category-scores">
          <h3>Бали за категоріями</h3>
          <div class="category-grid">
            <div class="category-card">
              <div class="category-header">
                <h4>Базові потреби</h4>
                <span class="category-score">{{ basicNeedsScore }}</span>
              </div>
              <div class="category-description">
                <p>Q1-Q2: Чіткість очікувань та ресурси</p>
              </div>
            </div>
            
            <div class="category-card">
              <div class="category-header">
                <h4>Індивідуальний</h4>
                <span class="category-score">{{ individualScore }}</span>
              </div>
              <div class="category-description">
                <p>Q3-Q6: Сильні сторони та розвиток</p>
              </div>
            </div>
            
            <div class="category-card">
              <div class="category-header">
                <h4>Зростання</h4>
                <span class="category-score">{{ growthScore }}</span>
              </div>
              <div class="category-description">
                <p>Q7-Q10: Вплив та місія</p>
              </div>
            </div>
            
            <div class="category-card">
              <div class="category-header">
                <h4>Командна робота</h4>
                <span class="category-score">{{ teamworkScore }}</span>
              </div>
              <div class="category-description">
                <p>Q11-Q12: Розвиток та дружба</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Детальні результати -->
        <div class="detailed-results">
          <h3>Детальні результати учасників</h3>
          <DataTable :value="gallupResults" :paginator="true" :rows="10" class="results-datatable">
            <Column field="user.username" header="Учасник">
              <template #body="slotProps">
                {{ slotProps.data.user?.username || 'Анонімний' }}
              </template>
            </Column>
            <Column field="overall_score" header="Загальний бал">
              <template #body="slotProps">
                <span class="score-display">{{ slotProps.data.overall_score }}</span>
              </template>
            </Column>
            <Column field="engagement_level" header="Рівень залуженості">
              <template #body="slotProps">
                <Tag :value="getEngagementLevelLabel(slotProps.data.engagement_level)" 
                     :severity="getEngagementSeverity(slotProps.data.engagement_level)" />
              </template>
            </Column>
            <Column field="completed_at" header="Дата завершення">
              <template #body="slotProps">
                {{ formatDate(slotProps.data.completed_at) }}
              </template>
            </Column>
            <Column header="Дії">
              <template #body="slotProps">
                <Button
                  icon="pi pi-eye"
                  class="p-button-text p-button-sm"
                  @click="viewUserResult(slotProps.data)"
                  v-tooltip.top="'Деталі результату'"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>

             <!-- Результати eNPS -->
       <div v-else-if="session?.test_type?.category === 'enps'" class="enps-results">
         <h2>Результати eNPS</h2>
         
         <!-- Загальний NPS бал -->
         <div class="overall-score-section">
           <div class="score-circle">
             <div class="circle-progress" :style="enpsScoreStyle">
               <div class="circle-fill"></div>
             </div>
                           <div class="score-value">
                <span class="score-number">{{ averageNpsScore }} %</span>
                <span class="score-label">eNPS бал</span>
              </div>
           </div>
           <div class="score-info">
             <h3>{{ getNpsLevelLabel(averageNpsLevel) }}</h3>
             <p>{{ getNpsLevelDescription(averageNpsLevel) }}</p>
           </div>
         </div>

                   <!-- Розподіл за категоріями -->
          <div class="category-scores">
            <h3>Розподіл за категоріями</h3>
            <p class="enps-formula">eNPS = Відсоток Промоутерів (%) - Відсоток Критиків (%)</p>
           <div class="category-grid">
             <div class="category-card">
               <div class="category-header">
                 <h4>Промоутери</h4>
                 <span class="category-score">{{ promoterPercentage }}%</span>
               </div>
               <div class="category-description">
                 <p>Оцінка 9-10: Високо задоволені та лояльні</p>
               </div>
             </div>
             
             <div class="category-card">
               <div class="category-header">
                 <h4>Пасивні</h4>
                 <span class="category-score">{{ passivePercentage }}%</span>
               </div>
               <div class="category-description">
                 <p>Оцінка 7-8: Задоволені, але нейтральні</p>
               </div>
             </div>
             
             <div class="category-card">
               <div class="category-header">
                 <h4>Критики</h4>
                 <span class="category-score">{{ detractorPercentage }}%</span>
               </div>
               <div class="category-description">
                 <p>Оцінка 0-6: Незадоволені та критичні</p>
               </div>
             </div>
           </div>
         </div>

         <!-- Детальні результати -->
         <div class="detailed-results">
           <h3>Детальні результати учасників</h3>
           <DataTable :value="enpsResults" :paginator="true" :rows="10" class="results-datatable">
             <Column field="user.username" header="Учасник">
               <template #body="slotProps">
                 {{ slotProps.data.user?.username || 'Анонімний' }}
               </template>
             </Column>
             <Column field="main_question_score" header="Оцінка">
               <template #body="slotProps">
                 <span class="score-display">{{ slotProps.data.main_question_score }}/10</span>
               </template>
             </Column>
             <Column field="category" header="Категорія">
               <template #body="slotProps">
                 <Tag :value="getCategoryLabel(slotProps.data.category)" 
                      :severity="getCategorySeverity(slotProps.data.category)" />
               </template>
             </Column>
             <Column field="completed_at" header="Дата завершення">
               <template #body="slotProps">
                 {{ formatDate(slotProps.data.completed_at) }}
               </template>
             </Column>
             <Column header="Дії">
               <template #body="slotProps">
                 <Button
                   icon="pi pi-eye"
                   class="p-button-text p-button-sm"
                   @click="viewUserResult(slotProps.data)"
                   v-tooltip.top="'Деталі результату'"
                 />
               </template>
             </Column>
           </DataTable>
         </div>
       </div>

      <!-- Загальні результати -->
      <div v-else class="general-results">
        <h2>Загальні результати</h2>
        <!-- Тут буде логіка для загальних результатів -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'

export default {
  name: 'SessionResults',
  data() {
    return {
      sessionId: null,
      session: null,
      gallupResults: [],
      enpsResults: []
    }
  },
  computed: {
    ...mapState(['results', 'sessions']),
    completionRate() {
      if (!this.session) return 0
      const participants = this.session.participants_count || 0
      const completed = this.session.completed_count || 0
      return participants > 0 ? Math.round((completed / participants) * 100) : 0
    },
    sessionPeriod() {
      if (!this.session) return 'N/A'
      const startDate = this.session.start_date ? new Date(this.session.start_date).toLocaleDateString('uk-UA') : 'N/A'
      const endDate = this.session.end_date ? new Date(this.session.end_date).toLocaleDateString('uk-UA') : 'N/A'
      return `${startDate} - ${endDate}`
    },
    averageOverallScore() {
      if (this.gallupResults.length === 0) return 'N/A'
      const scores = this.gallupResults
        .filter(r => r.overall_score !== null && r.overall_score !== undefined)
        .map(r => r.overall_score)
      if (scores.length === 0) return 'N/A'
      return (scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1)
    },
    averageEngagementLevel() {
      if (this.gallupResults.length === 0) return 'low'
      const scores = this.gallupResults
        .filter(r => r.overall_score !== null && r.overall_score !== undefined)
        .map(r => r.overall_score)
      if (scores.length === 0) return 'low'
      const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length
      
      if (avgScore >= 4.0) return 'high'
      if (avgScore >= 3.0) return 'medium'
      return 'low'
    },
    overallScoreAngle() {
      const score = parseFloat(this.averageOverallScore)
      if (isNaN(score)) return 0
      return (score / 5) * 360
    },
    overallScoreStyle() {
      const score = parseFloat(this.averageOverallScore)
      if (isNaN(score)) return {}
      const angle = (score / 5) * 360
      return {
        background: `conic-gradient(var(--primary-color) 0deg, var(--primary-color) ${angle}deg, var(--surface-border) ${angle}deg)`
      }
    },
    basicNeedsScore() {
      if (this.gallupResults.length === 0) return 'N/A'
      const scores = this.gallupResults
        .filter(r => r.basic_needs_score !== null && r.basic_needs_score !== undefined)
        .map(r => r.basic_needs_score)
      if (scores.length === 0) return 'N/A'
      return (scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1)
    },
    individualScore() {
      if (this.gallupResults.length === 0) return 'N/A'
      const scores = this.gallupResults
        .filter(r => r.individual_contribution_score !== null && r.individual_contribution_score !== undefined)
        .map(r => r.individual_contribution_score)
      if (scores.length === 0) return 'N/A'
      return (scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1)
    },
    growthScore() {
      if (this.gallupResults.length === 0) return 'N/A'
      const scores = this.gallupResults
        .filter(r => r.growth_score !== null && r.growth_score !== undefined)
        .map(r => r.growth_score)
      if (scores.length === 0) return 'N/A'
      return (scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1)
    },
         teamworkScore() {
       if (this.gallupResults.length === 0) return 'N/A'
       const scores = this.gallupResults
         .filter(r => r.teamwork_score !== null && r.teamwork_score !== undefined)
         .map(r => r.teamwork_score)
       if (scores.length === 0) return 'N/A'
       return (scores.reduce((sum, score) => sum + score, 0) / scores.length).toFixed(1)
     },
           // ENPS computed properties
      averageNpsScore() {
        if (this.enpsResults.length === 0) return 'N/A'
        const promoters = this.enpsResults.filter(r => r.main_question_score >= 9).length
        const detractors = this.enpsResults.filter(r => r.main_question_score <= 6).length
        const total = this.enpsResults.length
        
        if (total === 0) return 'N/A'
        
        const promoterPercentage = (promoters / total) * 100
        const detractorPercentage = (detractors / total) * 100
        const enpsScore = promoterPercentage - detractorPercentage
        
        return enpsScore.toFixed(1)
      },
           averageNpsLevel() {
        if (this.enpsResults.length === 0) return 'low'
        const enpsScore = parseFloat(this.averageNpsScore)
        if (isNaN(enpsScore)) return 'low'
        
        // eNPS рівні: відмінно (50+), добре (0-49), потребує покращення (-100 до -1)
        if (enpsScore >= 50) return 'excellent'
        if (enpsScore >= 0) return 'good'
        return 'poor'
      },
           enpsScoreStyle() {
        const score = parseFloat(this.averageNpsScore)
        if (isNaN(score)) return {}
        
        // eNPS може бути від -100 до +100, конвертуємо в 0-100 для кругової діаграми
        const normalizedPercentage = Math.max(0, Math.min(100, (score + 100) / 2))
        
        return {
          background: `conic-gradient(var(--primary-color) 0deg, var(--primary-color) ${normalizedPercentage}deg, var(--surface-border) ${normalizedPercentage}deg)`
        }
      },
     promoterPercentage() {
       if (this.enpsResults.length === 0) return 0
       const promoters = this.enpsResults.filter(r => r.main_question_score >= 9).length
       const percentage = Math.round((promoters / this.enpsResults.length) * 100)
       return percentage
     },
     passivePercentage() {
       if (this.enpsResults.length === 0) return 0
       const passives = this.enpsResults.filter(r => r.main_question_score >= 7 && r.main_question_score <= 8).length
       const percentage = Math.round((passives / this.enpsResults.length) * 100)
       return percentage
     },
     detractorPercentage() {
       if (this.enpsResults.length === 0) return 0
       const detractors = this.enpsResults.filter(r => r.main_question_score <= 6).length
       const percentage = Math.round((detractors / this.enpsResults.length) * 100)
       return percentage
     }
  },
  methods: {
    ...mapActions(['fetchResults', 'fetchSessions']),
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('uk-UA')
    },
    getEngagementLevelLabel(level) {
      const labels = {
        high: 'Висока залуженість',
        medium: 'Середня залуженість',
        low: 'Низька залуженість'
      }
      return labels[level] || level
    },
    getEngagementDescription(level) {
      const descriptions = {
        high: 'Відмінно! Продовжуйте в тому ж дусі.',
        medium: 'Добре, але є простір для покращення.',
        low: 'Потрібно звернути увагу на залуженість співробітників.'
      }
      return descriptions[level] || ''
    },
         getEngagementSeverity(level) {
       const severities = {
         high: 'success',
         medium: 'warning',
         low: 'danger'
       }
       return severities[level] || 'info'
     },
     // ENPS methods
     getNpsLevelLabel(level) {
       const labels = {
         excellent: 'Відмінно',
         good: 'Добре',
         poor: 'Потребує покращення'
       }
       return labels[level] || level
     },
           getNpsLevelDescription(level) {
        const descriptions = {
          excellent: 'Відмінний eNPS! Співробітники високо задоволені та лояльні.',
          good: 'Хороший eNPS. Є простір для покращення задоволеності.',
          poor: 'Низький eNPS. Потрібно звернути увагу на задоволеність співробітників.'
        }
        return descriptions[level] || ''
      },
     getCategoryLabel(category) {
       const labels = {
         promoter: 'Промоутер',
         passive: 'Пасивний',
         detractor: 'Критик'
       }
       return labels[category] || category
     },
     getCategorySeverity(category) {
       const severities = {
         promoter: 'success',
         passive: 'warning',
         detractor: 'danger'
       }
       return severities[category] || 'info'
     },
    viewUserResult() {
      // Навігація до детального результату користувача
    },
    async loadSessionData() {
      this.sessionId = this.$route.params.sessionId
      this.session = this.sessions.find(s => s.id == this.sessionId)
      
      if (!this.session) {
        await this.fetchSessions()
        this.session = this.sessions.find(s => s.id == this.sessionId)
      }
      
      // Завантажуємо результати для цієї сесії
      if (this.session?.test_type?.category === 'gallup_q12') {
        // Перевіряємо, чи є результати в store
        if (this.results.length === 0) {
          await this.fetchResults()
        }
        
        this.gallupResults = this.results
          .filter(r => r.session == this.sessionId && r.gallup_result)
          .map(r => ({
            ...r.gallup_result,
            user: r.user,
            completed_at: r.completed_at
          }))
        
                  if (this.gallupResults.length > 0) {
                    // Gallup results available
                  }
       }
       
       // Завантажуємо результати для ENPS
       if (this.session?.test_type?.category === 'enps') {
         if (this.results.length === 0) {
           await this.fetchResults()
         }
         
         this.enpsResults = this.results
           .filter(r => r.session == this.sessionId && r.enps_result)
           .map(r => ({
             ...r.enps_result,
             user: r.user,
             completed_at: r.completed_at
           }))
         
       }
     }
  },
  async mounted() {
    await Promise.all([
      this.fetchResults(),
      this.fetchSessions()
    ])
    await this.loadSessionData()
  }
}
</script>

<style scoped>
.session-results-page {
  padding: 2rem;
}

.page-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.back-button {
  margin-right: 1rem;
}

.header-info h1 {
  color: var(--primary-color);
  margin-bottom: 0.5rem;
}

.header-info p {
  color: var(--text-color-secondary);
  margin: 0;
}

.content-section {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--shadow);
  color: var(--text-color);
}

.session-stats {
  margin-bottom: 2rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  background: var(--surface-card);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.stat-content h3 {
  margin: 0;
  font-size: 1.5rem;
  color: var(--text-color);
}

.stat-content p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.gallup-results h2 {
  color: var(--text-color);
  margin-bottom: 2rem;
}

.overall-score-section {
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 3rem;
  padding: 2rem;
  background: var(--surface-ground);
  border-radius: 8px;
}

.score-circle {
  position: relative;
  width: 150px;
  height: 150px;
}

.circle-progress {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 8px solid var(--surface-border);
}

.circle-fill {
  position: absolute;
  top: 5px;
  left: 5px;
  right: 5px;
  bottom: 5px;
  border-radius: 50%;
  background: conic-gradient(var(--primary-color) 0deg, var(--primary-color) var(--score-degrees), var(--surface-border) var(--score-degrees), var(--surface-border) 360deg);
}

.score-value {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.score-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--primary-color);
}

.score-label {
  display: block;
  font-size: 0.9rem;
  color: var(--text-color-secondary);
}

.score-info h3 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
}

.score-info p {
  margin: 0;
  color: var(--text-color-secondary);
}

.category-scores {
  margin-bottom: 3rem;
}

.category-scores h3 {
  color: var(--text-color);
  margin-bottom: 1rem;
}

.enps-formula {
  color: var(--text-color-secondary);
  font-style: italic;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.category-card {
  padding: 1.5rem;
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  background: var(--surface-card);
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.category-header h4 {
  margin: 0;
  color: var(--text-color);
}

.category-score {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary-color);
}

.category-description p {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.detailed-results h3 {
  color: var(--text-color);
  margin-bottom: 1.5rem;
}

.results-datatable {
  margin-top: 1rem;
}

.score-display {
  font-weight: 600;
  color: var(--primary-color);
}

@media (max-width: 768px) {
  .session-results-page {
    padding: 1rem;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .overall-score-section {
    flex-direction: column;
    text-align: center;
  }
  
  .category-grid {
    grid-template-columns: 1fr;
  }
}
</style> 